import localforage from 'localforage';
import { DEVICE_ID_STORAGE_KEY } from './deviceRegistry';

// List of all LocalForage/IndexedDB stores used in the app
const STORES_TO_CLEAR = [
  'Products',
  'Customers',
  'Tasks',
  'Comments',
  'Reports',
  'Images',
  'TodayVisits',
  'keyvaluepairs', // Default localforage store
];

// LocalStorage keys to preserve (don't clear these)
const PRESERVE_LOCALSTORAGE_KEYS = [
  'doc', // Document/organization info
  DEVICE_ID_STORAGE_KEY, // Device registry identity must survive logout
  'enablePerfMonitoring',
  'viewMode', // User preferences
];

/**
 * Clears all user-specific data from IndexedDB and LocalStorage
 * Safe to call - won't break the app
 */
export const clearUserData = async () => {
  console.log('Starting user data cleanup...');
  
  try {
    // 1. Clear all LocalForage/IndexedDB stores
    const clearPromises = STORES_TO_CLEAR.map(async (storeName) => {
      try {
        const store = localforage.createInstance({ name: storeName });
        await store.clear();
        console.log(`✓ Cleared IndexedDB store: ${storeName}`);
      } catch (error) {
        console.warn(`Failed to clear store ${storeName}:`, error);
      }
    });

    await Promise.allSettled(clearPromises);

    // 2. Clear LocalStorage (except preserved keys)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !PRESERVE_LOCALSTORAGE_KEYS.includes(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        console.log(`✓ Cleared localStorage: ${key}`);
      } catch (error) {
        console.warn(`Failed to clear localStorage key ${key}:`, error);
      }
    });

    // 3. Clear SessionStorage
    try {
      sessionStorage.clear();
      console.log('✓ Cleared SessionStorage');
    } catch (error) {
      console.warn('Failed to clear SessionStorage:', error);
    }

    // 4. Clear any remaining IndexedDB databases manually
    await clearIndexedDBDatabases();

    console.log('✓ User data cleanup completed successfully');
    return true;

  } catch (error) {
    console.error('Error during user data cleanup:', error);
    return false;
  }
};

/**
 * Manually clear IndexedDB databases by name
 */
const clearIndexedDBDatabases = async () => {
  if (!window.indexedDB) {
    console.warn('IndexedDB not supported');
    return;
  }

  const databasesToCheck = [
    'localforage',
    'Products',
    'Customers',
    'Tasks',
    'Comments',
    'Reports',
    'Images',
    'TodayVisits',
  ];

  const clearPromises = databasesToCheck.map(async (dbName) => {
    try {
      // Try to delete the database
      const deleteRequest = indexedDB.deleteDatabase(dbName);
      
      return new Promise((resolve, reject) => {
        deleteRequest.onsuccess = () => {
          console.log(`✓ Deleted IndexedDB: ${dbName}`);
          resolve();
        };
        
        deleteRequest.onerror = () => {
          console.warn(`Could not delete IndexedDB: ${dbName}`);
          resolve(); // Don't reject - it's okay if DB doesn't exist
        };
        
        deleteRequest.onblocked = () => {
          console.warn(`IndexedDB deletion blocked: ${dbName}`);
          resolve(); // Don't reject - just continue
        };
      });
    } catch (error) {
      console.warn(`Error deleting IndexedDB ${dbName}:`, error);
    }
  });

  await Promise.allSettled(clearPromises);
};

/**
 * Quick cleanup for testing purposes
 * Only clears cache data, preserves user preferences
 */
export const clearCacheOnly = async () => {
  console.log('Clearing cache data only...');
  
  const cacheStores = ['Products', 'Customers', 'Tasks', 'Comments', 'Reports'];
  
  const clearPromises = cacheStores.map(async (storeName) => {
    try {
      const store = localforage.createInstance({ name: storeName });
      await store.clear();
      console.log(`✓ Cleared cache: ${storeName}`);
    } catch (error) {
      console.warn(`Failed to clear cache ${storeName}:`, error);
    }
  });

  await Promise.allSettled(clearPromises);
  
  // Clear performance metrics and temp data
  const cacheKeys = ['perfMetrics', 'filterCache', 'searchHistory'];
  cacheKeys.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to clear ${key}:`, error);
    }
  });

  console.log('✓ Cache cleanup completed');
};

/**
 * Check storage usage before/after cleanup
 */
export const getStorageInfo = async () => {
  const info = {
    indexedDB: await getIndexedDBUsage(),
    localStorage: getLocalStorageUsage(),
    sessionStorage: getSessionStorageUsage(),
  };
  
  return info;
};

const getIndexedDBUsage = async () => {
  if (!navigator.storage || !navigator.storage.estimate) {
    return { error: 'Storage API not supported' };
  }
  
  try {
    const estimate = await navigator.storage.estimate();
    return {
      quota: estimate.quota,
      usage: estimate.usage,
      usageMB: Math.round((estimate.usage || 0) / (1024 * 1024) * 100) / 100,
      quotaMB: Math.round((estimate.quota || 0) / (1024 * 1024) * 100) / 100,
    };
  } catch (error) {
    return { error: error.message };
  }
};

const getLocalStorageUsage = () => {
  try {
    let totalSize = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage.getItem(key).length;
      }
    }
    return {
      totalSize,
      totalSizeKB: Math.round(totalSize / 1024 * 100) / 100,
      itemCount: localStorage.length,
    };
  } catch (error) {
    return { error: error.message };
  }
};

const getSessionStorageUsage = () => {
  try {
    let totalSize = 0;
    for (let key in sessionStorage) {
      if (sessionStorage.hasOwnProperty(key)) {
        totalSize += sessionStorage.getItem(key).length;
      }
    }
    return {
      totalSize,
      totalSizeKB: Math.round(totalSize / 1024 * 100) / 100,
      itemCount: sessionStorage.length,
    };
  } catch (error) {
    return { error: error.message };
  }
};
